import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { adminSectionFromPath, isAdminPath } from "./adminNav";
import type { Transcript, TranscriptState, TranscriptSummary } from "./types";
import { AdminRequests } from "./ui/AdminRequests";
import { AdminView } from "./ui/AdminView";
import { UsageBadge, type Me } from "./ui/UsageBadge";
import { UserMenu } from "./ui/UserMenu";
import { Chat } from "./ui/Chat";
import { Library } from "./ui/Library";
import { TranscriptView } from "./ui/TranscriptView";
import {
  canChat,
  formatDateTime,
  formatTimestamp,
  POLL_INTERVAL_MS,
  shouldPoll,
  transcriptionAnnouncement,
} from "./ui/helpers";
import {
  transcriptExportContent,
  transcriptExportFilename,
  transcriptToText,
  type TranscriptExportFormat,
} from "./ui/transcriptExport";

type Theme = "dark" | "light";

// voic chip lockup: two-tone slash mark. Arm colors are themed via CSS
// (.brand .logo .arm-*) — powder chip in dark, white chip in light.
function BrandMark() {
  return (
    <span className="logo">
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <polygon className="arm-l" points="16,16 34,16 58,84 40,84" />
        <polygon className="arm-r" points="84,16 66,16 42,84 60,84" />
      </svg>
    </span>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 2v7" />
      <path d="M5 6l3 3 3-3" />
      <path d="M3 11v2h10v-2" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M6 3h4" />
      <path d="M5 5h6v8H5z" />
      <path d="M4 4h8" />
    </svg>
  );
}

function TranscriptToggleIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      {collapsed ? (
        <>
          <path d="M4.5 4.5h7v7h-7z" />
          <path d="M6.5 6.5h3" />
        </>
      ) : (
        <path d="M4 11.5h8" className="heavy" />
      )}
    </svg>
  );
}

export function App() {
  const [transcripts, setTranscripts] = useState<TranscriptSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTranscript, setSelectedTranscript] = useState<Transcript | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [showTranscript, setShowTranscript] = useState(true);
  // Sidebar is always visible on desktop (CSS); on mobile it's an off-canvas
  // drawer toggled by this flag. Selecting a recording closes it.
  const [navOpen, setNavOpen] = useState(false);
  // null = auth not yet known; false = gated (show login); true = in.
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  // Mobile only (CSS-gated): which pane shows when a recording is open. Desktop
  // always shows both, so this is inert above 720px.
  const [view, setView] = useState<"recording" | "chat" | "both">("both");
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const saved = localStorage.getItem("voic-theme");
      return saved === "dark" || saved === "light" ? saved : "dark";
    } catch {
      return "dark";
    }
  });

  // Theme lives on <html> so the CSS tokens swap; persisted across reloads.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("voic-theme", theme);
    } catch {
      /* storage unavailable — theme still applies for this session */
    }
  }, [theme]);

  // Escape closes the mobile drawer (the scrim is pointer-only).
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/transcripts");
      if (res.status === 401) {
        setAuthed(false); // no/expired cookie — drop to the login screen
        return;
      }
      if (res.ok) {
        setAuthed(true);
        setTranscripts((await res.json()) as TranscriptSummary[]);
      }
    } catch {
      /* transient — next poll retries */
    }
  }, []);

  const logout = useCallback(async () => {
    await fetch("/api/logout", { method: "POST" }).catch(() => {});
    setAuthed(false);
  }, []);

  // Exit impersonation: the server swaps the cookie back to an admin session,
  // then a full navigation reloads everything as the admin.
  const stopImpersonating = useCallback(async () => {
    await fetch("/api/impersonate/stop", { method: "POST" }).catch(() => {});
    window.location.href = "/admin/users";
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Count this page display (/app or /admin). sendBeacon is fire-and-forget
  // and survives navigation; analytics must never block or break the app.
  useEffect(() => {
    try {
      navigator.sendBeacon("/api/events", JSON.stringify({ path: window.location.pathname }));
    } catch {
      /* analytics only */
    }
  }, []);

  // Once signed in, load the account: admin flag (gates the requests panel) and
  // credits/usage (the usage badge). Refetched whenever the library refreshes,
  // since transcription completion moves the credit balance.
  const loadMe = useCallback(async () => {
    try {
      const res = await fetch("/api/me");
      if (!res.ok) return;
      const m = (await res.json()) as Me;
      setMe(m);
      setIsAdmin(!!m.isAdmin);
    } catch {
      /* transient */
    }
  }, []);
  useEffect(() => {
    if (authed) void loadMe();
  }, [authed, transcripts, loadMe]);

  // /admin/* is the same SPA bundle; non-admins get bounced back to the app.
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/app";
  const isAdminRoute = isAdminPath(currentPath);

  // Poll only while something is processing; stop once everything settles. Skip
  // on /admin — AdminView never shows the transcript library, so there's nothing
  // to keep fresh there.
  useEffect(() => {
    if (isAdminRoute || !shouldPoll(transcripts)) return;
    const timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isAdminRoute, transcripts, refresh]);

  useEffect(() => {
    if (isAdminRoute && me && !me.isAdmin) window.location.replace("/app");
  }, [isAdminRoute, me]);

  // Announce transcriptions settling (done/failed) to screen readers; the
  // visual chip flips silently otherwise. prevStates is null until the first
  // fetch so pre-existing transcripts don't announce on load.
  const [announcement, setAnnouncement] = useState("");
  const prevStates = useRef<Map<string, TranscriptState> | null>(null);
  useEffect(() => {
    const msg = transcriptionAnnouncement(prevStates.current, transcripts);
    prevStates.current = new Map(transcripts.map((t) => [t.id, t.state]));
    if (msg) setAnnouncement(msg);
  }, [transcripts]);

  const selected = transcripts.find((t) => t.id === selectedId) ?? null;
  const transcriptReady = selected?.state === "done" && !!selectedTranscript && selectedTranscript.segments.length > 0;
  const transcriptCollapsed = selected?.state === "done" && !showTranscript;

  useEffect(() => {
    let cancelled = false;
    setSelectedTranscript(null);
    setCopyState("idle");
    if (!selected) return;

    fetch(`/api/transcripts/${selected.id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((t: Transcript | null) => {
        if (!cancelled) setSelectedTranscript(t);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [selected?.id, selected?.state]);

  useEffect(() => {
    setShowTranscript(true);
  }, [selected?.id]);

  // Title tracks the open recording: helps screen readers, window switching,
  // and voice commands tell tabs apart.
  useEffect(() => {
    document.title = selected ? `${selected.name}, voic` : "voic";
    return () => {
      document.title = "voic";
    };
  }, [selected?.name]);

  useEffect(() => {
    if (copyState === "idle") return;
    const timer = window.setTimeout(() => setCopyState("idle"), 1600);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  function saveTranscript(format: TranscriptExportFormat) {
    if (!selectedTranscript) return;
    const blob = new Blob([transcriptExportContent(selectedTranscript, format)], {
      type: format === "md" ? "text/markdown;charset=utf-8" : "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = transcriptExportFilename(selectedTranscript, format);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function copyTranscript() {
    if (!selectedTranscript) return;
    try {
      await navigator.clipboard.writeText(transcriptToText(selectedTranscript));
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  if (authed === null) return null; // brief auth probe — avoid flashing the app
  if (authed === false) return <Login />;
  if (isAdminRoute) return me?.isAdmin ? <AdminView section={adminSectionFromPath(currentPath)} /> : null; // null while me loads or redirecting

  return (
    <div className="app">
      <div className="sr-only" role="status">
        {announcement}
      </div>
      {me?.impersonatedBy && (
        <div className="impersonation-banner" role="status">
          <span>Viewing as {me.email}. Actions run as this user.</span>
          <button onClick={() => void stopImpersonating()}>Exit</button>
        </div>
      )}
      <header className="topbar">
        <div className="topbar-left">
          <button
            className="nav-toggle"
            aria-label="Toggle recordings"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((o) => !o)}
          >
            ☰
          </button>
          <div className="brand">
            <BrandMark />
            <h1 className="word">voic</h1>
          </div>
        </div>
        <div className="topbar-right">
          <div className="theme-toggle" role="group" aria-label="Theme">
            <button
              className={theme === "dark" ? "active" : ""}
              aria-label="Dark theme"
              aria-pressed={theme === "dark"}
              onClick={() => setTheme("dark")}
            >
              ☾
            </button>
            <button
              className={theme === "light" ? "active" : ""}
              aria-label="Light theme"
              aria-pressed={theme === "light"}
              onClick={() => setTheme("light")}
            >
              ☀
            </button>
          </div>
          <UserMenu email={me?.email ?? null} isAdmin={!!me?.isAdmin} onSignOut={() => void logout()} />
        </div>
      </header>

      <div className="body">
        <aside className={navOpen ? "sidebar open" : "sidebar"}>
          <Library
            transcripts={transcripts}
            selectedId={selectedId}
            onSelect={(id) => {
              setSelectedId(id);
              setNavOpen(false); // reveal the transcript on mobile
            }}
            onUploaded={refresh}
            onChanged={refresh}
            maxRecordingMinutes={me?.limits.minutesPerRecording ?? 30}
          />
          <UsageBadge me={me} />
          {isAdmin && <AdminRequests />}
        </aside>
        {navOpen && <div className="scrim" onClick={() => setNavOpen(false)} />}

        <main
          className={transcriptCollapsed ? "main transcript-collapsed" : "main"}
          data-view={selected && canChat(selected.state) ? view : "recording"}
        >
          {selected ? (
            <>
              <div className="transcript-head">
                <div className="transcript-heading">
                  <h2 className="transcript-title">{selected.name}</h2>
                  <div className="transcript-meta">
                    {selected.audioDurationSecs != null && (
                      <span>{formatTimestamp(selected.audioDurationSecs)}</span>
                    )}
                    {selected.audioDurationSecs != null && <span>·</span>}
                    <span>uploaded {formatDateTime(selected.createdAt)}</span>
                  </div>
                </div>
                <div className="recording-controls" role="group" aria-label="Recording controls">
                  <button
                    type="button"
                    className="recording-control"
                    aria-label="Save transcript as .txt"
                    disabled={!transcriptReady}
                    onClick={() => saveTranscript("txt")}
                  >
                    <DownloadIcon />
                    <span>.txt</span>
                  </button>
                  <button
                    type="button"
                    className="recording-control"
                    aria-label="Save transcript as .md"
                    disabled={!transcriptReady}
                    onClick={() => saveTranscript("md")}
                  >
                    <DownloadIcon />
                    <span>.md</span>
                  </button>
                  <button
                    type="button"
                    className="recording-control"
                    aria-label="Copy transcript to clipboard"
                    disabled={!transcriptReady}
                    onClick={() => void copyTranscript()}
                  >
                    <CopyIcon />
                    <span>{copyState === "copied" ? "Copied" : copyState === "failed" ? "Failed" : "Copy"}</span>
                    {/* announces the Copied/Failed flip; empty on reset, so no echo */}
                    <span className="sr-only" role="status">
                      {copyState === "copied" ? "Copied" : copyState === "failed" ? "Copy failed" : ""}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="recording-control icon-only"
                    aria-label={showTranscript ? "Hide transcript" : "Show transcript"}
                    aria-expanded={showTranscript}
                    disabled={selected.state !== "done"}
                    onClick={() => setShowTranscript((show) => !show)}
                    title={showTranscript ? "Minimize transcript" : "Maximize transcript"}
                  >
                    <TranscriptToggleIcon collapsed={!showTranscript} />
                  </button>
                </div>
              </div>
              {canChat(selected.state) && (
                <div className="view-switch" role="group" aria-label="Show">
                  {(["recording", "chat", "both"] as const).map((v) => (
                    <button
                      key={v}
                      className={view === v ? "active" : ""}
                      aria-pressed={view === v}
                      onClick={() => setView(v)}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              )}
              <div className={transcriptCollapsed ? "main-scroll transcript-drawer collapsed" : "main-scroll transcript-drawer"}>
                {!transcriptCollapsed && <TranscriptView transcript={selectedTranscript} state={selected.state} />}
              </div>
              {canChat(selected.state) && <Chat key={selected.id} transcriptId={selected.id} />}
            </>
          ) : (
            <p className="placeholder">Select a transcript, or upload audio to begin.</p>
          )}
        </main>
      </div>
    </div>
  );
}

// Auth gate: one card with a Sign in / Create account segmented toggle.
// - Sign in posts to /api/login for a passwordless magic link.
// - Create account posts to /api/access-requests (an admin reviews it).
function Login() {
  const [mode, setMode] = useState<"signin" | "register">("signin");

  // role=tablist promises arrow-key switching; with two tabs either arrow
  // moves to the other one, and focus follows selection (roving tabindex).
  const onTabKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const next = mode === "signin" ? "register" : "signin";
    setMode(next);
    e.currentTarget.querySelector<HTMLButtonElement>(`[data-mode="${next}"]`)?.focus();
  };

  return (
    <div className="login">
      <div className="login-card">
        <div className="brand">
          <BrandMark />
          <h1 className="word">voic</h1>
        </div>
        <div className="login-tabs" role="tablist" aria-label="Sign in or create account" onKeyDown={onTabKey}>
          <button
            type="button"
            role="tab"
            data-mode="signin"
            aria-selected={mode === "signin"}
            tabIndex={mode === "signin" ? 0 : -1}
            className={mode === "signin" ? "active" : ""}
            onClick={() => setMode("signin")}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            data-mode="register"
            aria-selected={mode === "register"}
            tabIndex={mode === "register" ? 0 : -1}
            className={mode === "register" ? "active" : ""}
            onClick={() => setMode("register")}
          >
            Create account
          </button>
        </div>
        {mode === "signin" ? <SignInForm /> : <RegisterForm />}
      </div>
    </div>
  );
}

// Magic-link sign in: enter your email, get a one-time link. The server's
// response is deliberately identical whether or not the email has an account,
// so the UI never reveals membership — it just confirms the request was sent.
function SignInForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  if (sent) {
    return <p className="login-sent">Check your email for a sign-in link. It expires in 15 minutes.</p>;
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const value = email.trim();
        if (!value) return;
        await fetch("/api/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: value }),
        }).catch(() => {});
        setSent(true); // same confirmation regardless of the outcome
      }}
    >
      <input
        autoFocus
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        aria-label="Work email"
      />
      <button className="btn-primary" type="submit">
        Email me a sign-in link
      </button>
      <p className="login-helper">We'll email a secure link to sign in. Passwordless, every time.</p>
    </form>
  );
}

// Self-service access request: enter your email, pass the captcha (Turnstile —
// only rendered when a site key is configured), and an admin reviews it. The
// confirmation is generic and reveals nothing about membership.
function RegisterForm() {
  const [email, setEmail] = useState("");
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    fetch("/api/config")
      .then((r) => r.json())
      .then((c: { turnstileSiteKey: string | null }) => setSiteKey(c.turnstileSiteKey))
      .catch(() => {});
  }, []);

  // Load the Turnstile widget script once, only if a site key exists. Turnstile
  // auto-renders any `.cf-turnstile` div and injects the token as a hidden
  // `cf-turnstile-response` field in the surrounding form.
  useEffect(() => {
    if (!siteKey || document.getElementById("cf-turnstile-script")) return;
    const s = document.createElement("script");
    s.id = "cf-turnstile-script";
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    s.async = true;
    document.head.appendChild(s);
  }, [siteKey]);

  if (sent) {
    return <p className="login-sent">Thanks, your request was submitted. An admin will review it.</p>;
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const value = email.trim();
        if (!value || !acceptedTerms) return;
        const token = String(new FormData(e.currentTarget).get("cf-turnstile-response") ?? "");
        await fetch("/api/access-requests", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: value, captchaToken: token }),
        }).catch(() => {});
        setSent(true);
      }}
    >
      <input
        autoFocus
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        aria-label="Work email"
      />
      {siteKey && <div className="cf-turnstile" data-sitekey={siteKey} />}
      <label className="terms-check">
        <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />
        <span>
          I agree to the <a href="/terms">terms</a>.
        </span>
      </label>
      <button className="btn-primary" type="submit" disabled={!acceptedTerms}>
        Request access
      </button>
      <p className="login-helper">We'll review your request and email a secure link once you're approved.</p>
    </form>
  );
}
